import React from "react"
import { Bar, BarChart, Cell, LabelList, XAxis, YAxis, Tooltip } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "~/shadcn/ui/card"
import { ChartContainer } from "~/shadcn/ui/chart"
import type { StatusPoint } from "./VolumeChart"

const STATUS_BUCKETS = [
  { key: "ok" as const, label: "200 OK", color: "#22c55e" },
  { key: "rateLimited" as const, label: "429 Rate Limited", color: "#f59e0b" },
  { key: "badRequest" as const, label: "400 Bad Request", color: "#6b7280" },
  { key: "timeout" as const, label: "5xx Timeout", color: "#ef4444" },
]

export const SuccessRateChart: React.FC<{ data: StatusPoint[] }> = ({
  data,
}) => {
  const totals = STATUS_BUCKETS.map(({ key, label, color }) => ({
    label,
    color,
    count: data.reduce((s, p) => s + p[key], 0),
  }))

  const total = totals.reduce((s, b) => s + b.count, 0)

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-500">
          Status Code Breakdown
        </CardTitle>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <div className="flex h-[280px] items-center justify-center text-sm text-gray-400">
            No data for this period
          </div>
        ) : (
          <ChartContainer
            className="h-[280px] w-full"
            config={Object.fromEntries(
              STATUS_BUCKETS.map((b) => [
                b.key,
                { label: b.label, color: b.color },
              ])
            )}
          >
            <BarChart
              data={totals}
              layout="vertical"
              margin={{ top: 4, right: 80, bottom: 4, left: 4 }}
            >
              <XAxis
                type="number"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={115}
              />
              <Tooltip
                content={({ payload }) => {
                  if (!payload?.length) return null
                  const entry = payload[0]?.payload
                  const pct =
                    total > 0 ? ((entry.count / total) * 100).toFixed(1) : "0.0"
                  return (
                    <div className="rounded border bg-white px-2 py-1 text-xs shadow-sm">
                      <div
                        className="font-medium"
                        style={{ color: entry.color }}
                      >
                        {entry.label}
                      </div>
                      <div>
                        {Number(entry.count).toLocaleString()} ({pct}%)
                      </div>
                    </div>
                  )
                }}
              />
              <Bar
                dataKey="count"
                isAnimationActive={false}
                radius={[0, 3, 3, 0]}
              >
                {totals.map((entry) => (
                  <Cell key={entry.label} fill={entry.color} />
                ))}
                <LabelList
                  dataKey="count"
                  position="right"
                  formatter={(v: number) =>
                    `${v.toLocaleString()} (${
                      total > 0 ? ((v / total) * 100).toFixed(0) : 0
                    }%)`
                  }
                  style={{ fontSize: 11 }}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}
